import React, { useState, useEffect, useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, Checkbox, IconButton, TextField, InputAdornment, MenuItem,
  Select, Chip, Avatar, Dialog, DialogTitle, DialogContent, Menu, DialogActions,
  Card, CardContent, Divider, useTheme
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Helmet } from 'react-helmet';
import SectionLoader from "../components/SectionLoader";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import EmailIcon from "@mui/icons-material/Email";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PhoneIcon from "@mui/icons-material/Phone";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import GroupsIcon from "@mui/icons-material/Groups";
import SecurityIcon from "@mui/icons-material/Security";
import { Tooltip, Grid } from "@mui/material";
import { useUser } from "../context/UserContext";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { FormControl, InputLabel } from '@mui/material';
import Toast from "../components/Toast";
import ConfirmDialog from "../components/ConfirmDialog";
import { getDisplayRoleName } from "../utils/roleUtils";
import TimelineIcon from "@mui/icons-material/Timeline";
import UserActivity from "./UserActivity";




function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const Users = () => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  // View Mode: 'users' (User Management) vs 'activity' (User Activity Tracking)
  const [activeView, setActiveView] = useState("users");
  const [activityUserFilter, setActivityUserFilter] = useState("");
  const [selectedActivityUser, setSelectedActivityUser] = useState(null);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All Status");
  const [role, setRole] = useState("All Roles");
  const [selected, setSelected] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [sortBy, setSortBy] = useState("name_asc");
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState(null); // or orgOptions[0] for default
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [editUser, setEditUser] = useState(null);
  const { user: currentUser, orgViewMode } = useUser();
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [Bulkstatus, setBulkstatus] = useState(1); // Default status for adding to My Songs
  const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations];
  const API_URL = process.env.REACT_APP_API_URL || 'http://45.194.3.128:4000/api/admin/';
  const [loading, setLoading] = useState(true);
  const [editErrors, setEditErrors] = useState({});
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [userGroupRoles, setUserGroupRoles] = useState([]); // [{group_id, group_name, role_id, group_user_id}]
  const [groupRolesLoading, setGroupRolesLoading] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]); // all groups in the org
  const [addGroupForm, setAddGroupForm] = useState({ group_id: '', role_id: '' }); // new group role form
  const [group, setGroup] = useState('All Groups');
  const [allGroups, setAllGroups] = useState([]);

  // User Details Modal states (Requirement 1)
  const [selectedUserForDetails, setSelectedUserForDetails] = useState(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);

  const handleOpenUserDetails = (userObj) => {
    setSelectedUserForDetails(userObj);
    setUserDetailsOpen(true);
  };

  const handleCloseUserDetails = () => {
    setUserDetailsOpen(false);
    setSelectedUserForDetails(null);
  };

  const handleCopyText = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setToast({ open: true, message: `${label} copied to clipboard!`, severity: 'success' });
  };

  // Handle "Select All"
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(users.map((user) => user.id)); // select all IDs
    } else {
      setSelected([]); // deselect all
    }
  };

  // Handle individual select
  const handleSelect = (id) => {
    setSelected((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((selectedId) => selectedId !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  // ✅ Derive selectedUsers from selected IDs
  const selectedUsers = allUsers
    .filter((user) => selected.includes(user.id))
    .map((user) => ({
      id: user.id,
      name: `${user.app_users?.first_name || ''} ${user.app_users?.last_name || ''}`.trim(),
    }));


  const BulkUpdateToUser = async () => {
    if (selected.length === 0) {
      setToast({ open: true, message: "Please select at least one user.", severity: "warning" });
      return;
    }
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}bulk-update-users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        users: selected,
        status: Bulkstatus,
      }),
    });
    const data = await response.json();

    if (!response.ok || data.success === false) {
      // 🔑 Handle invalid token
      if (data.message === 'Invalid token') {
        localStorage.removeItem('token');
        window.location.href = '/login'; // redirect to login
        return;
      }


    }

    if (response.ok) {
      const message = Bulkstatus === 1 ? "Users verified successfully" : Bulkstatus === 2 ? "Users declined successfully" : Bulkstatus === 4 ? "Users activated successfully" : "Users deactivated successfully";
      setToast({ open: true, message: message, severity: "success" });
      setOpenViewDialog(false);
      setSelected([]);
      //  setselectedUsers([]);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } else {
      setToast({ open: true, message: data?.message || 'Failed to update users', severity: "error" });
    }
  };


  useEffect(() => {
    const fetchUsers = async (isRetry = false) => {

      try {
        setLoading(true);
        let token = localStorage.getItem('token');
        // Only filter by organization if explicitly in orgViewMode or selected from dropdown.
        // In standard Super Admin view (or 'global' mode), pass null so all users are returned.
        const isGlobalMode = !orgViewMode?.orgId || orgViewMode.orgId === 'global' || orgViewMode.orgId === 0;
        const activeOrgId = (!isGlobalMode && orgViewMode?.orgId)
          ? orgViewMode.orgId
          : (organization && organization.id !== 0 ? organization.id : null);

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Fetch page 1 with limit 100 to pull the master dataset
        let response = await fetch(`${API_URL}users-list`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            search: '',
            status: 'All Status',
            role: 'All Roles',
            group: 'All Groups',
            organization: activeOrgId,
            page: 1,
            limit: 100
          })
        });

        // Auto-heal: If remote server returned 500 when Super Admin is in global view (token has org_id: null),
        // refresh token via switch-organization with org_id: 1 so Prisma doesn't crash on null org_id
        const isGlobalOrgMode = !orgViewMode?.orgId || orgViewMode?.orgId === 'global' || orgViewMode?.orgId === 0;
        if (response.status === 500 && !isRetry && isGlobalOrgMode && currentUser?.role === 1) {
          try {
            const refreshRes = await fetch(`${API_URL}switch-organization`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ org_id: 1 })
            });
            const refreshData = await refreshRes.json();
            if (refreshData?.success && refreshData?.token) {
              localStorage.setItem('token', refreshData.token);
              const curUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
              curUserInfo.organization = 'Glocal Prayer Network';
              curUserInfo.organization_logo = null;
              curUserInfo.role = 1;
              localStorage.setItem('userInfo', JSON.stringify(curUserInfo));

              return fetchUsers(true);
            }
          } catch (healErr) {
            console.warn('Auto-heal token attempt failed:', healErr);
          }
        }

        const data = await response.json();

        if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login'; // redirect to login
            return;
          }
          setAllUsers([]);
        } else {
          let aggregated = Array.isArray(data.users) ? [...data.users] : [];
          const totalCount = data.total ?? aggregated.length;
          const totalPagesServer = Math.ceil(totalCount / 100);

          if (totalPagesServer > 1) {
            const fetchPromises = [];
            for (let p = 2; p <= Math.min(totalPagesServer, 20); p++) {
              fetchPromises.push(
                fetch(`${API_URL}users-list`, {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({
                    search: '',
                    status: 'All Status',
                    role: 'All Roles',
                    group: 'All Groups',
                    organization: activeOrgId,
                    page: p,
                    limit: 100
                  })
                })
                .then(res => res.json())
                .then(resData => (resData && (resData.success !== false) && Array.isArray(resData.users)) ? resData.users : [])
                .catch(() => [])
              );
            }
            const extraPages = await Promise.all(fetchPromises);
            extraPages.forEach(batch => {
              aggregated = aggregated.concat(batch);
            });
          }

          // De-duplicate by ID
          const map = new Map();
          aggregated.forEach(u => {
            if (u && u.id) map.set(String(u.id), u);
          });
          setAllUsers(Array.from(map.values()));
        }

      } catch (err) {
        console.error('Fetch users error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [organization, API_URL, orgViewMode, currentUser]);

  const handleRequestSort = (property) => {
    let nextSort;
    if (sortBy === `${property}_asc`) {
      nextSort = `${property}_desc`;
    } else {
      nextSort = `${property}_asc`;
    }
    setSortBy(nextSort);
    setPage(1);
  };

  // Master Sorting & Filtering across entire dataset
  const filteredAndSortedUsers = useMemo(() => {
    let list = [...allUsers];

    // Status filter
    if (status !== 'All Status') {
      list = list.filter(u => u.account_status === status);
    }

    // Role filter
    if (role !== 'All Roles') {
      list = list.filter(u => String(u.role) === String(role) || String(u.roles?.id) === String(role));
    }

    // Organization filter
    if (orgViewMode?.orgId && orgViewMode.orgId !== 'global' && orgViewMode.orgId !== 0) {
      const targetOrgId = Number(orgViewMode.orgId);
      list = list.filter(u =>
        Number(u.org_id) === targetOrgId ||
        Number(u.origanisation?.id) === targetOrgId ||
        Number(u.organisations?.id) === targetOrgId
      );
    } else if (organization && organization.id !== 0) {
      const targetOrgId = Number(organization.id);
      list = list.filter(u =>
        Number(u.org_id) === targetOrgId ||
        Number(u.origanisation?.id) === targetOrgId ||
        Number(u.organisations?.id) === targetOrgId
      );
    }

    // Group filter
    if (group && group !== 'All Groups') {
      const targetGroupId = Number(group);
      list = list.filter(u => {
        const groupUsers = u.app_users?.group_users || [];
        return groupUsers.some(gu => Number(gu.group_id) === targetGroupId);
      });
    }

    // Search query filter across all fields
    if (search.trim()) {
      const term = search.toLowerCase().trim();
      list = list.filter(u => {
        const first = (u.app_users?.first_name || '').toLowerCase();
        const last = (u.app_users?.last_name || '').toLowerCase();
        const fullName = `${first} ${last}`.trim();
        const email = (u.app_users?.email || '').toLowerCase();
        const phone = (u.app_users?.phone || u.app_users?.phone_no || '').toLowerCase();
        const orgName = (u.origanisation?.org_name || u.organisations?.org_name || '').toLowerCase();
        const roleName = (u.roles?.role || '').toLowerCase();
        return fullName.includes(term) || email.includes(term) || phone.includes(term) || orgName.includes(term) || roleName.includes(term);
      });
    }

    // Dynamic Master Alphabetical Sorting (Default: A -> Z)
    list.sort((a, b) => {
      const firstA = (a.app_users?.first_name || '').trim();
      const lastA = (a.app_users?.last_name || '').trim();
      const nameA = `${firstA} ${lastA}`.trim().toLowerCase();

      const firstB = (b.app_users?.first_name || '').trim();
      const lastB = (b.app_users?.last_name || '').trim();
      const nameB = `${firstB} ${lastB}`.trim().toLowerCase();

      if (sortBy === 'name_asc') {
        return nameA.localeCompare(nameB);
      }
      if (sortBy === 'name_desc') {
        return nameB.localeCompare(nameA);
      }
      if (sortBy === 'org_asc') {
        const orgA = (a.origanisation?.org_name || a.organisations?.org_name || '').toLowerCase();
        const orgB = (b.origanisation?.org_name || b.organizations?.org_name || '').toLowerCase();
        return orgA.localeCompare(orgB);
      }
      if (sortBy === 'org_desc') {
        const orgA = (a.origanisation?.org_name || a.organisations?.org_name || '').toLowerCase();
        const orgB = (b.origanisation?.org_name || b.organizations?.org_name || '').toLowerCase();
        return orgB.localeCompare(orgA);
      }
      if (sortBy === 'role_asc') {
        const roleA = (a.roles?.role || '').toLowerCase();
        const roleB = (b.roles?.role || '').toLowerCase();
        return roleA.localeCompare(roleB);
      }
      if (sortBy === 'role_desc') {
        const roleA = (a.roles?.role || '').toLowerCase();
        const roleB = (b.roles?.role || '').toLowerCase();
        return roleB.localeCompare(roleA);
      }
      if (sortBy === 'status_asc') {
        const stA = (a.account_status || '').toLowerCase();
        const stB = (b.account_status || '').toLowerCase();
        return stA.localeCompare(stB);
      }
      if (sortBy === 'status_desc') {
        const stA = (a.account_status || '').toLowerCase();
        const stB = (b.account_status || '').toLowerCase();
        return stB.localeCompare(stA);
      }
      if (sortBy === 'date_desc') {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      if (sortBy === 'date_asc') {
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      }

      // Default fallback: A -> Z
      return nameA.localeCompare(nameB);
    });

    return list;
  }, [allUsers, status, role, orgViewMode, organization, group, search, sortBy]);

  const totalItems = filteredAndSortedUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const users = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAndSortedUsers.slice(start, start + pageSize);
  }, [filteredAndSortedUsers, page, pageSize]);

  useEffect(() => {
    const fetchRolesAndOrganizations = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}roles-organizations`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login'; // redirect to login
            return;
          }
        }

        if (response && response.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }

        setRoles(data.roles || []);
        setOrganizations(data.organizations || []);
        setAllGroups(data.allGroups || []);
      } catch (error) {
        console.error("Error fetching roles and organizations:", error);
      }
    };

    fetchRolesAndOrganizations();
  }, [API_URL]);


  const handleEdit = (id) => {
    setEditUser(allUsers.find((user) => user.id === id) || users.find((user) => user.id === id));
  };

  // ...existing code...


  // Validation function
  const validateEditUser = () => {
    const errors = {};
    // First Name
    if (!editUser.app_users.first_name || editUser.app_users.first_name.trim() === "") {
      errors.first_name = "First name is required";
    } else if (editUser.app_users.first_name.trim().length < 2) {
      errors.first_name = "Minimum 2 characters required";
    } else if (!/^[A-Za-z\s]+$/.test(editUser.app_users.first_name.trim())) {
      errors.first_name = "First name should contain characters only";
    }
    // Last Name
    if (!editUser.app_users.last_name || editUser.app_users.last_name.trim() === "") {
      errors.last_name = "Last name is required";
    } else if (editUser.app_users.last_name.trim().length < 2) {
      errors.last_name = "Minimum 2 characters required";
    } else if (!/^[A-Za-z\s]+$/.test(editUser.app_users.last_name.trim())) {
      errors.last_name = "Last name should contain characters only";
    }
    // Email
    if (!editUser.app_users.email || editUser.app_users.email.trim() === "") {
      errors.email = "Email is required";
    } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(editUser.app_users.email)) {
      errors.email = "Valid email is required";
    }
    // Phone
    if (!editUser.app_users.phone || editUser.app_users.phone.trim() === "") {
      errors.phone = "Phone number is required";
    } else if (!/^\d{10,15}$/.test(editUser.app_users.phone.trim())) {
      errors.phone = "Valid phone number is required";
    } else if (!/^\d+$/.test(editUser.app_users.phone.trim())) {
      errors.phone = "Phone number should contain numbers only";
    }
    // Logo/Profile Picture size validation
    if (
      editUser.app_users.profile_pic &&
      editUser.app_users.profile_pic instanceof File &&
      editUser.app_users.profile_pic.size > 200 * 1024
    ) {
      errors.profile_pic = "Image size should be less than 200KB";
    }
    return errors;
  };

  const handleSaveEdit = async () => {
    const errors = validateEditUser();
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append("first_name", editUser.app_users.first_name);
    formData.append("last_name", editUser.app_users.last_name);
    formData.append("email", editUser.app_users.email);
    formData.append("phone", editUser.app_users.phone);
    formData.append("address", editUser.app_users.address);
    formData.append("gender", editUser.app_users.gender);
    formData.append("baptized", editUser.app_users.baptized);
    formData.append("date_of_birth", editUser.app_users.date_of_birth);

    if (editUser.app_users.profile_pic && editUser.app_users.profile_pic instanceof File) {
      formData.append("profile_pic", editUser.app_users.profile_pic);
    }
    formData.append("id", editUser.user_id);

    const response = await fetch(`${API_URL}update-user`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData
    });
    const data = await response.json();
    if (!response.ok || data.success === false) {
      // 🔑 Handle invalid token
      if (data.message === 'Invalid token') {
        localStorage.removeItem('token');
        window.location.href = '/login'; // redirect to login
        return;
      }


    }
    if (response.ok) {
      setToast({ open: true, message: "User updated successfully", severity: "success" });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } else {
      if (data.message === 'Invalid token') {
        localStorage.removeItem('token');
        window.location.href = '/login'; // redirect to login
        return;
      }
      setToast({ open: true, message: "Update failed: " + (data?.message || 'Unknown error'), severity: "error" });
    }
  };
  // ...existing code...


  // ...existing code...
  const handleActivate = async (id, type, status, user_id) => {
    const token = localStorage.getItem('token');

    let verify_status = '';
    if (type === 'AV') {
      verify_status = 'Account';
    } else {
      verify_status = 'Background';
    }

    setConfirmDialog({
      open: true,
      title: 'Confirm Action',
      message: `Are you sure you want to ${status} this user ${verify_status}?`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });

        const response = await fetch(`${API_URL}update-user-status`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: id, status: status, type: type, user_id: user_id })
        });
        const data = await response.json();

        if (!response.ok || data.success === false) {
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

          setToast({ open: true, message: "Update failed: " + (data?.message || 'Unknown error'), severity: "error" });
        }
        setToast({ open: true, message: "User updated successfully", severity: "success" });
        setTimeout(() => {
          window.location.reload();
        }, 1500);

      }
    });
  };

  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleSelectOption = (status) => {
    setOpenViewDialog(true);
    setBulkstatus(status);
  }
  const handleViewDialogClose = () => {
    setOpenViewDialog(false);
  };

  const handleRoleClick = async (user) => {
    setSelectedUserForRole(user);
    setNewRole(user.role);
    setUserGroupRoles([]);
    setRoleDialogOpen(true);
    // Fetch user's group memberships
    setGroupRolesLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Fetch user's current group memberships
      const [groupRolesRes, availableGroupsRes] = await Promise.all([
        fetch(`${API_URL}user-group-roles`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: user.user_id, org_id: user.org_id })
        }),
        fetch(`${API_URL}org-groups`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ org_id: user.org_id })
        })
      ]);
      const groupRolesData = await groupRolesRes.json();
      const availableGroupsData = await availableGroupsRes.json();
      if (groupRolesRes.ok && groupRolesData.success) setUserGroupRoles(groupRolesData.groupRoles);
      if (availableGroupsRes.ok && availableGroupsData.success) setAvailableGroups(availableGroupsData.groups);
    } catch (err) {
      console.error('Error fetching group data:', err);
    } finally {
      setGroupRolesLoading(false);
    }
  };

  const handleGroupRoleChange = (groupUserId, newRoleId) => {
    setUserGroupRoles(prev =>
      prev.map(g => g.group_user_id === groupUserId ? { ...g, role_id: newRoleId } : g)
    );
  };

  const handleGroupRoleUpdateClick = (groupEntry) => {
    setRoleDialogOpen(false);
    setConfirmDialog({
      open: true,
      title: 'Confirm Group Role Change',
      message: `Are you sure you want to update the role for ${selectedUserForRole.app_users.first_name} in "${groupEntry.group_name}"?`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        await updateGroupRole(groupEntry.group_user_id, groupEntry.role_id);
      }
    });
  };

  const updateGroupRole = async (groupUserId, roleId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}update-group-user-role`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ group_user_id: groupUserId, role_id: roleId })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setToast({ open: true, message: data.message, severity: 'success' });
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setToast({ open: true, message: data.message || 'Failed to update group role', severity: 'error' });
      }
    } catch (error) {
      setToast({ open: true, message: 'Server error', severity: 'error' });
    }
  };

  const handleRemoveGroupRole = (groupEntry) => {
    setRoleDialogOpen(false);
    setConfirmDialog({
      open: true,
      title: 'Confirm Remove from Group',
      message: `Are you sure you want to remove ${selectedUserForRole?.app_users.first_name} from "${groupEntry.group_name}"?`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}remove-group-user-role`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ group_user_id: groupEntry.group_user_id })
          });
          const data = await response.json();
          if (response.ok && data.success) {
            setToast({ open: true, message: data.message, severity: 'success' });
            setTimeout(() => window.location.reload(), 1500);
          } else {
            setToast({ open: true, message: data.message || 'Failed to remove from group', severity: 'error' });
          }
        } catch (err) {
          setToast({ open: true, message: 'Server error', severity: 'error' });
        }
      }
    });
  };

  const handleAddGroupRole = () => {
    if (!addGroupForm.group_id || !addGroupForm.role_id) {
      setToast({ open: true, message: 'Please select both a group and a role.', severity: 'warning' });
      return;
    }
    const groupName = availableGroups.find(g => g.id === addGroupForm.group_id)?.name || 'this group';
    const targetRole = roles.find(r => r.id === addGroupForm.role_id);
    const roleName = targetRole ? getDisplayRoleName(targetRole) : 'this role';
    setRoleDialogOpen(false);
    setConfirmDialog({
      open: true,
      title: 'Confirm Add Group Role',
      message: `Are you sure you want to assign ${selectedUserForRole?.app_users.first_name} as "${roleName}" in "${groupName}"?`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}add-group-user-role`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: selectedUserForRole.user_id,
              group_id: addGroupForm.group_id,
              role_id: addGroupForm.role_id,
              org_id: selectedUserForRole.org_id
            })
          });
          const data = await response.json();
          if (response.ok && data.success) {
            setToast({ open: true, message: data.message, severity: 'success' });
            setTimeout(() => window.location.reload(), 1500);
          } else {
            setToast({ open: true, message: data.message || 'Failed to add group role', severity: 'error' });
          }
        } catch (err) {
          setToast({ open: true, message: 'Server error', severity: 'error' });
        }
      }
    });
  };

  return (
    <>
      <Helmet>
        <title>Users - Glocal Prayer Network</title>
      </Helmet>
      <Dialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: isLight ? '#FFFFFF' : 'rgba(30, 41, 59, 0.98)',
            border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2.5,
            color: isLight ? '#0F172A' : '#FFFFFF',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, color: isLight ? '#0F172A' : '#FFFFFF' }}>
          Manage Roles
          {selectedUserForRole && (
            <Typography variant="body2" sx={{ color: isLight ? '#64748B' : 'text.secondary', mt: 0.5 }}>
              {selectedUserForRole.app_users.first_name} {selectedUserForRole.app_users.last_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.1)' }}>
          {selectedUserForRole && (
            <>
              {currentUser?.role != 5 && (

                <>

                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: isLight ? '#0D9488' : '#4CA1AF' }}>
                    Organization Role
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={newRole}
                        label="Role"
                        onChange={(e) => setNewRole(e.target.value)}
                        disabled={selectedUserForRole.account_status !== 'Accepted' || selectedUserForRole.isVerified != 1}
                      >
                        {roles.filter((r) => r.id === "2" || r.id === "3" || r.id === String(selectedUserForRole?.role)).map((r) => (
                          <MenuItem key={r.id} value={r.id}>{getDisplayRoleName(r)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {selectedUserForRole.account_status === 'Accepted' && selectedUserForRole.isVerified == 1 && (
                      <>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={!newRole || String(newRole) === String(selectedUserForRole?.role)}
                          onClick={() => {
                            if (!newRole) {
                              setToast({ open: true, message: 'Please select a role first.', severity: 'warning' });
                              return;
                            }
                            setRoleDialogOpen(false);
                            setConfirmDialog({
                              open: true,
                              title: 'Confirm Organization Role Change',
                              message: `Are you sure you want to change the organization role for ${selectedUserForRole?.app_users.first_name}?`,
                              onConfirm: async () => {
                                setConfirmDialog(prev => ({ ...prev, open: false }));
                                const token = localStorage.getItem('token');
                                const response = await fetch(`${API_URL}update-user-role`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ id: selectedUserForRole.id, role_id: newRole })
                                });
                                const data = await response.json();
                                if (response.ok && data.success) {
                                  setToast({ open: true, message: data.message, severity: 'success' });
                                  setTimeout(() => window.location.reload(), 1500);
                                } else {
                                  setToast({ open: true, message: data.message || 'Failed to update role', severity: 'error' });
                                }
                              }
                            });
                          }}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          Update
                        </Button>
                        {/* Only show Remove if user currently has a special org role (2=Org Admin, 3=Group Admin) */}
                        {(String(selectedUserForRole?.role) === '2' || String(selectedUserForRole?.role) === '3') && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() => {
                              setRoleDialogOpen(false);
                              setConfirmDialog({
                                open: true,
                                title: 'Confirm Remove Organization Role',
                                message: `Are you sure you want to remove the organization role for ${selectedUserForRole?.app_users.first_name}? They will be reset to Org Member.`,
                                onConfirm: async () => {
                                  setConfirmDialog(prev => ({ ...prev, open: false }));
                                  const token = localStorage.getItem('token');
                                  const response = await fetch(`${API_URL}update-user-role`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ id: selectedUserForRole.id, role_id: 4 })
                                  });
                                  const data = await response.json();
                                  if (response.ok && data.success) {
                                    setToast({ open: true, message: 'Role removed. User is now an Org Member.', severity: 'success' });
                                    setTimeout(() => window.location.reload(), 1500);
                                  } else {
                                    setToast({ open: true, message: data.message || 'Failed to remove role', severity: 'error' });
                                  }
                                }
                              });
                            }}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            Remove
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </>
              )}
              {/* ── Group Roles ── */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: isLight ? '#0D9488' : '#4CA1AF' }}>
                Group Roles
              </Typography>
              {groupRolesLoading ? (
                <Typography variant="body2" sx={{ color: isLight ? '#64748B' : 'text.secondary', py: 1 }}>Loading group memberships…</Typography>
              ) : userGroupRoles.length === 0 ? (
                <Typography variant="body2" sx={{ color: isLight ? '#64748B' : 'text.secondary', py: 1 }}>This user is not a member of any group.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {userGroupRoles.map((g) => (
                    <Box key={g.group_user_id} sx={{
                      display: 'flex', gap: 2, alignItems: 'center',
                      p: 1.5, borderRadius: 1.5,
                      border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.1)',
                      background: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.03)'
                    }}>
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, color: isLight ? '#0F172A' : '#FFFFFF' }}>{g.group_name}</Typography>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Role</InputLabel>
                        <Select
                          value={g.role_id}
                          label="Role"
                          onChange={(e) =>
                            handleGroupRoleChange(g.group_user_id, e.target.value)
                          }
                          disabled={selectedUserForRole.account_status !== 'Accepted' || selectedUserForRole.isVerified != 1}
                        >
                          {roles
                            .filter((r) => r.id === "5" || r.id === "6" || r.id === String(g.role_id))
                            .map((r) => (
                              <MenuItem key={r.id} value={r.id}>
                                {getDisplayRoleName(r)}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleGroupRoleUpdateClick(g)}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Update
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => handleRemoveGroupRole(g)}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Remove
                      </Button>

                    </Box>
                  ))}
                </Box>
              )}

              {/* ── Add Group Role ── */}

              {selectedUserForRole.account_status === 'Accepted' && selectedUserForRole.isVerified == 1 && (


                <Box sx={{
                  mt: 3, pt: 2,
                  borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.1)'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: isLight ? '#0D9488' : '#4CA1AF' }}>
                    Add Group Role
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ flex: 1, minWidth: 150 }}>
                      <InputLabel>Group</InputLabel>
                      <Select
                        value={addGroupForm.group_id}
                        label="Group"
                        onChange={(e) => setAddGroupForm(prev => ({ ...prev, group_id: e.target.value }))}
                      >
                        {availableGroups.length === 0 ? (
                          <MenuItem disabled value="">No groups found</MenuItem>
                        ) : (
                          availableGroups.map((g) => (
                            <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ flex: 1, minWidth: 150 }}>
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={addGroupForm.role_id}
                        label="Role"
                        onChange={(e) => setAddGroupForm(prev => ({ ...prev, role_id: e.target.value }))}
                      >
                        {roles
                          .filter((r) => r.id === "5" || r.id === "6")
                          .map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                              {getDisplayRoleName(r)}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      size="small"
                      color="success"
                      onClick={handleAddGroupRole}
                      sx={{ whiteSpace: 'nowrap', px: 3 }}
                    >
                      + Add
                    </Button>
                  </Box>
                </Box>

              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Box>
        {/* Header */}
        <Box sx={{
          mb: 3,
          pb: 2,
          pt: 2,
          background: isLight
            ? '#FFFFFF'
            : 'linear-gradient(135deg, rgba(76, 161, 175, 0.15) 0%, rgba(44, 62, 80, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(76, 161, 175, 0.3)',
          boxShadow: isLight ? '0 2px 8px rgba(15, 23, 42, 0.05)' : '0 4px 12px rgba(76, 161, 175, 0.2)',
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: isLight ? '#0F172A' : '#FFFFFF', mb: 0.5, letterSpacing: '-0.5px' }}>
              User Management
            </Typography>
            <Typography variant="body2" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
              Manage user accounts, approvals, access permissions, and community activity tracking.
            </Typography>
          </Box>

          {/* View Mode Switcher: Users List vs User Activity */}
          <Box display="flex" alignItems="center" gap={1} bgcolor={isLight ? '#F1F5F9' : 'rgba(255,255,255,0.06)'} p={0.6} borderRadius={2.5}>
            <Button
              size="small"
              variant={activeView === 'users' ? 'contained' : 'text'}
              startIcon={<PersonIcon />}
              onClick={() => {
                setActiveView('users');
                setActivityUserFilter('');
                setSelectedActivityUser(null);
              }}
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                textTransform: 'none',
                bgcolor: activeView === 'users' ? '#0D9488' : 'transparent',
                color: activeView === 'users' ? '#FFFFFF' : (isLight ? '#475569' : '#94A3B8'),
                boxShadow: activeView === 'users' ? '0 2px 6px rgba(13, 148, 136, 0.3)' : 'none',
                '&:hover': { bgcolor: activeView === 'users' ? '#0F766E' : (isLight ? '#E2E8F0' : 'rgba(255,255,255,0.1)') }
              }}
            >
              Users List ({allUsers.length || 0})
            </Button>
            <Button
              size="small"
              variant={activeView === 'activity' ? 'contained' : 'text'}
              startIcon={<TimelineIcon />}
              onClick={() => {
                setSelectedActivityUser(null);
                setActivityUserFilter('');
                setActiveView('activity');
              }}
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                textTransform: 'none',
                bgcolor: activeView === 'activity' ? '#7C3AED' : 'transparent',
                color: activeView === 'activity' ? '#FFFFFF' : (isLight ? '#475569' : '#94A3B8'),
                boxShadow: activeView === 'activity' ? '0 2px 6px rgba(124, 58, 237, 0.3)' : 'none',
                '&:hover': { bgcolor: activeView === 'activity' ? '#6D28D9' : (isLight ? '#E2E8F0' : 'rgba(255,255,255,0.1)') }
              }}
            >
              User Activity
            </Button>
          </Box>
        </Box>

        {activeView === 'activity' ? (
          <UserActivity
            initialSearch={activityUserFilter}
            selectedUser={selectedActivityUser}
            onClearUser={() => {
              setSelectedActivityUser(null);
              setActivityUserFilter('');
            }}
            onBackToUsers={() => {
              setActiveView('users');
              setActivityUserFilter('');
              setSelectedActivityUser(null);
            }}
          />
        ) : (
          <>

        {/* Search and Filters */}
        <Paper sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(20px)',
          border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
          boxShadow: isLight ? '0 2px 8px rgba(15, 23, 42, 0.05)' : 'none',
        }}>
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{
                fontWeight: 700,
                color: isLight ? '#0F172A' : '#FFFFFF',
                fontSize: { xs: '1rem', sm: '1.125rem' },
              }}>
                Users List
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                {[
                  { label: 'All', value: 'All Status' },
                  { label: 'Active', value: 'Accepted', color: '#10B981' },
                  { label: 'Pending', value: 'Pending', color: '#F59E0B' },
                  { label: 'Inactive', value: 'Declined', color: '#EF4444' },
                ].map((s) => {
                  const isSel = status === s.value;
                  return (
                    <Chip
                      key={s.value}
                      label={s.label}
                      size="small"
                      onClick={() => {
                        setStatus(s.value);
                        setPage(1);
                      }}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: isSel ? 700 : 500,
                        backgroundColor: isLight
                          ? isSel
                            ? s.value === 'Accepted'
                              ? '#ECFDF5'
                              : s.value === 'Pending'
                              ? '#FFFBEB'
                              : s.value === 'Declined'
                              ? '#FEF2F2'
                              : '#F0FDFA'
                            : '#F8FAFC'
                          : isSel
                          ? s.color || '#4CA1AF'
                          : 'rgba(255, 255, 255, 0.08)',
                        color: isLight
                          ? isSel
                            ? s.value === 'Accepted'
                              ? '#059669'
                              : s.value === 'Pending'
                              ? '#D97706'
                              : s.value === 'Declined'
                              ? '#DC2626'
                              : '#0D9488'
                            : '#64748B'
                          : isSel
                          ? '#FFFFFF'
                          : 'rgba(255, 255, 255, 0.7)',
                        border: isLight
                          ? isSel
                            ? '1.5px solid ' +
                              (s.value === 'Accepted'
                                ? '#A7F3D0'
                                : s.value === 'Pending'
                                ? '#FDE68A'
                                : s.value === 'Declined'
                                ? '#FECACA'
                                : '#99F6E4')
                            : '1.5px solid #CBD5E1'
                          : isSel
                          ? `1px solid ${s.color || '#4CA1AF'}`
                          : '1px solid rgba(255, 255, 255, 0.15)',
                        '&:hover': {
                          backgroundColor: isLight
                            ? isSel
                              ? undefined
                              : '#F1F5F9'
                            : isSel
                            ? s.color || '#4CA1AF'
                            : 'rgba(255, 255, 255, 0.15)',
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
            <Box sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: 'wrap',
              width: { xs: '100%', sm: 'auto' }
            }}>
              <TextField
                size="small"
                placeholder="Search users by name, email, or organization..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  width: { xs: '100%', sm: 350 },
                  minWidth: { xs: '100%', sm: 'auto' }
                }}
              />
              <Select
                size="small"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                sx={{ width: { xs: '100%', sm: 130 } }}
              >
                <MenuItem value="All Status">All Status</MenuItem>
                <MenuItem value="Accepted">Active</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Declined">Inactive</MenuItem>
              </Select>

              <Select
                size="small"
                value={role}
                onChange={(e) => {
                  setRole(e.target.value);
                  setPage(1);
                }}
                sx={{ width: { xs: '100%', sm: 130 } }}
              >
                <MenuItem value="All Roles">All Roles</MenuItem>
                {roles
                  .filter(role => !["5", "6"].includes(role.id))
                  .map(role => (
                    <MenuItem key={role.id} value={role.id}>
                      {getDisplayRoleName(role)}
                    </MenuItem>
                  ))
                }
              </Select>
              {currentUser?.role === 1 && !orgViewMode && (

                <Autocomplete
                  size="small"
                  value={organization}
                  onChange={(event, newValue) => {
                    setOrganization(newValue);
                    setPage(1);
                  }}
                  options={orgOptions}
                  getOptionLabel={(option) => option?.org_name || ""}
                  renderInput={(params) => <TextField {...params} label="Organization" />}
                  sx={{ width: { xs: '100%', sm: 230 } }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}

              <Select
                size="small"
                value={group}
                onChange={(e) => {
                  setGroup(e.target.value);
                  setPage(1);
                }}
                sx={{ width: { xs: '100%', sm: 130 } }}
              >
                <MenuItem value="All Groups">All Groups</MenuItem>
                {allGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                ))}
              </Select>

              <FormControl size="small" sx={{ width: { xs: '100%', sm: 165 } }}>
                <InputLabel sx={{ color: isLight ? '#475569' : undefined, fontWeight: 700, fontSize: "0.82rem" }}>
                  Sort By
                </InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                  sx={{
                    borderRadius: "8px",
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                    color: isLight ? "#0F172A" : "#FFFFFF",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: isLight ? '#CBD5E1' : 'rgba(255, 255, 255, 0.15)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0D9488' },
                  }}
                >
                  <MenuItem value="name_asc" sx={{ fontWeight: 600 }}>🔤 Name: A → Z</MenuItem>
                  <MenuItem value="name_desc" sx={{ fontWeight: 600 }}>🔤 Name: Z → A</MenuItem>
                  <MenuItem value="org_asc" sx={{ fontWeight: 600 }}>🏢 Organization: A → Z</MenuItem>
                  <MenuItem value="role_asc" sx={{ fontWeight: 600 }}>👥 Role: A → Z</MenuItem>
                  <MenuItem value="status_asc" sx={{ fontWeight: 600 }}>⏳ Status: A → Z</MenuItem>
                  <MenuItem value="date_desc" sx={{ fontWeight: 600 }}>📅 Joined: Newest</MenuItem>
                  <MenuItem value="date_asc" sx={{ fontWeight: 600 }}>📅 Joined: Oldest</MenuItem>
                </Select>
              </FormControl>

            </Box>
          </Box>
        </Paper>

        {/* Table */}
        <Paper sx={{
          borderRadius: 3,
          overflow: 'hidden',
          background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(20px)',
          border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
          boxShadow: isLight ? '0 2px 8px rgba(15, 23, 42, 0.05)' : 'none',
        }}>
          {/* Desktop Table View */}
          <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">

                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={users.length > 0 && selected.length === users.length}
                        indeterminate={selected.length > 0 && selected.length < users.length}
                        onChange={handleSelectAll}
                      />
                      <IconButton size="small" onClick={handleMenuOpen}>
                        <ArrowDropDownIcon />
                      </IconButton>

                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                      >
                        <MenuItem onClick={() => { handleSelectOption(1); handleMenuClose(); }}>
                          Background Verify
                        </MenuItem>
                        <MenuItem onClick={() => { handleSelectOption(2); handleMenuClose(); }}>
                          Background Decline
                        </MenuItem>

                        <MenuItem onClick={() => { handleSelectOption(4); handleMenuClose(); }}>
                          Account Activate
                        </MenuItem>
                        <MenuItem onClick={() => { handleSelectOption(5); handleMenuClose(); }}>
                          Account Deactivate
                        </MenuItem>

                      </Menu>
                    </Box>

                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy.startsWith('name')}
                      direction={sortBy === 'name_asc' ? 'asc' : 'desc'}
                      onClick={() => handleRequestSort('name')}
                      sx={{ fontWeight: 800, fontSize: '0.8rem', color: isLight ? '#0F172A' : '#FFFFFF' }}
                    >
                      USER
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy.startsWith('org')}
                      direction={sortBy === 'org_asc' ? 'asc' : 'desc'}
                      onClick={() => handleRequestSort('org')}
                      sx={{ fontWeight: 800, fontSize: '0.8rem', color: isLight ? '#0F172A' : '#FFFFFF' }}
                    >
                      ORGANIZATION
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy.startsWith('role')}
                      direction={sortBy === 'role_asc' ? 'asc' : 'desc'}
                      onClick={() => handleRequestSort('role')}
                      sx={{ fontWeight: 800, fontSize: '0.8rem', color: isLight ? '#0F172A' : '#FFFFFF' }}
                    >
                      ROLE
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy.startsWith('status')}
                      direction={sortBy === 'status_asc' ? 'asc' : 'desc'}
                      onClick={() => handleRequestSort('status')}
                      sx={{ fontWeight: 800, fontSize: '0.8rem', color: isLight ? '#0F172A' : '#FFFFFF' }}
                    >
                      STATUS
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy.startsWith('date')}
                      direction={sortBy === 'date_asc' ? 'asc' : 'desc'}
                      onClick={() => handleRequestSort('date')}
                      sx={{ fontWeight: 800, fontSize: '0.8rem', color: isLight ? '#0F172A' : '#FFFFFF' }}
                    >
                      JOINED
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: '0.8rem', color: isLight ? '#0F172A' : '#FFFFFF' }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Sectional loader overlay for table body */}
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ height: 200, textAlign: 'center', border: 'none' }}>
                      <SectionLoader open={loading} height={180} />
                    </TableCell>
                  </TableRow>
                ) : null}
                {/* Table rows */}
                {users.length > 0 ? (
                  users.map((user) => (
                    <TableRow
                      key={user.id}
                      hover
                      onClick={() => handleOpenUserDetails(user)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          bgcolor: isLight ? '#F0FDFA !important' : 'rgba(13, 148, 136, 0.1) !important',
                        },
                        '& td': {
                          borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.06)',
                          py: 1.5,
                          color: isLight ? '#1E293B' : '#F1F5F9',
                        }
                      }}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selected.includes(user.id)}
                          onChange={() => handleSelect(user.id, user.app_users.first_name + " " + user.app_users.last_name)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box 
                          sx={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: 1.5,
                            cursor: "pointer",
                            p: 0.5,
                            borderRadius: 1.5,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: isLight ? "rgba(13, 148, 136, 0.08)" : "rgba(76, 161, 175, 0.12)",
                              "& .user-fullname": { color: isLight ? "#0D9488" : "#4CA1AF" }
                            }
                          }}
                          onClick={() => handleOpenUserDetails(user)}
                          title="Click to view full user details"
                        >
                          <Avatar
                            src={user.app_users.profile_pic || undefined}
                            alt={user.app_users.first_name}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              background: isLight
                                ? "#F0FDFA"
                                : "linear-gradient(135deg, #203A43 0%, #2C5364 100%)",
                              color: isLight ? "#0D9488" : "#4CA1AF",
                              fontWeight: 700,
                              fontSize: 18,
                              border: isLight ? "1.5px solid #99F6E4" : "1px solid rgba(76, 161, 175, 0.3)",
                              flexShrink: 0,
                            }}
                            imgProps={{
                              onError: (e) => {
                                e.target.onerror = null;
                                e.target.src = "";
                              },
                            }}
                          >
                            {user.app_users.first_name ? user.app_users.first_name[0].toUpperCase() : <PersonIcon sx={{ fontSize: 20 }} />}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography 
                              className="user-fullname"
                              fontWeight={700} 
                              noWrap 
                              sx={{ 
                                color: isLight ? "#0F172A" : "#FFFFFF", 
                                fontSize: "0.95rem",
                                transition: "color 0.2s ease",
                                textDecoration: "underline",
                                textDecorationColor: "transparent",
                                "&:hover": { textDecorationColor: isLight ? "#0D9488" : "#4CA1AF" }
                              }}
                            >
                              {user.app_users.first_name} {user.app_users.last_name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: isLight ? '#334155' : 'text.secondary', fontWeight: 500, fontSize: '0.85rem' }} noWrap>
                              {user.app_users.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: isLight ? '#0F172A' : 'inherit', fontWeight: 600 }}>{user.origanisation.org_name}</TableCell>
                      <TableCell sx={{ color: isLight ? '#0F172A' : 'inherit', fontWeight: 600 }}>
                        {group !== "All Groups"
                          ? user.app_users?.group_users?.map(g => getDisplayRoleName(g.roles?.role, g.role_id)).join(", ") || "-"
                          : getDisplayRoleName(user.roles?.role, user.role)}
                      </TableCell>

                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={1}>
                          {/* Account Status */}
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon sx={{ fontSize: 16, color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)' }} titleAccess="Account Status" />
                            <Chip
                              label={
                                user.account_status === "Accepted"
                                  ? "Active"
                                  : user.account_status === "Pending"
                                    ? "Pending"
                                    : "Inactive"
                              }
                              size="small"
                              sx={{
                                fontSize: "0.7rem",
                                height: 22,
                                background: isLight
                                  ? user.account_status === "Accepted"
                                    ? "#ECFDF5"
                                    : user.account_status === "Pending"
                                      ? "#FFFBEB"
                                      : "#FEF2F2"
                                  : user.account_status === "Accepted"
                                    ? "rgba(16, 185, 129, 0.2)"
                                    : user.account_status === "Pending"
                                      ? "rgba(245, 158, 11, 0.2)"
                                      : "rgba(239, 68, 68, 0.2)",
                                color: isLight
                                  ? user.account_status === "Accepted"
                                    ? "#059669"
                                    : user.account_status === "Pending"
                                      ? "#D97706"
                                      : "#DC2626"
                                  : user.account_status === "Accepted"
                                    ? "#10B981"
                                    : user.account_status === "Pending"
                                      ? "#F59E0B"
                                      : "#EF4444",
                                fontWeight: 600,
                                border: isLight ? '1.5px solid' : '1px solid',
                                borderColor: isLight
                                  ? user.account_status === "Accepted"
                                    ? "#A7F3D0"
                                    : user.account_status === "Pending"
                                      ? "#FDE68A"
                                      : "#FECACA"
                                  : user.account_status === "Accepted"
                                    ? "rgba(16, 185, 129, 0.3)"
                                    : user.account_status === "Pending"
                                      ? "rgba(245, 158, 11, 0.3)"
                                      : "rgba(239, 68, 68, 0.3)",
                              }}
                            />
                          </Box>

                          {/* Background Status */}
                          <Box display="flex" alignItems="center" gap={1}>
                            <VerifiedUserIcon sx={{ fontSize: 16, color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)' }} titleAccess="Background Verification" />
                            <Chip
                              label={
                                user.isVerified === 1
                                  ? "Verified"
                                  : user.isVerified === 0
                                    ? "Pending"
                                    : "Declined"
                              }
                              size="small"
                              sx={{
                                fontSize: "0.7rem",
                                height: 22,
                                background: isLight
                                  ? user.isVerified === 1
                                    ? "#ECFDF5"
                                    : user.isVerified === 0
                                      ? "#FFFBEB"
                                      : "#FEF2F2"
                                  : user.isVerified === 1
                                    ? "rgba(16, 185, 129, 0.2)"
                                    : user.isVerified === 0
                                      ? "rgba(245, 158, 11, 0.2)"
                                      : "rgba(239, 68, 68, 0.2)",
                                color: isLight
                                  ? user.isVerified === 1
                                    ? "#059669"
                                    : user.isVerified === 0
                                      ? "#D97706"
                                      : "#DC2626"
                                  : user.isVerified === 1
                                    ? "#10B981"
                                    : user.isVerified === 0
                                      ? "#F59E0B"
                                      : "#EF4444",
                                fontWeight: 600,
                                border: isLight ? '1.5px solid' : '1px solid',
                                borderColor: isLight
                                  ? user.isVerified === 1
                                    ? "#A7F3D0"
                                    : user.isVerified === 0
                                      ? "#FDE68A"
                                      : "#FECACA"
                                  : user.isVerified === 1
                                    ? "rgba(16, 185, 129, 0.3)"
                                    : user.isVerified === 0
                                      ? "rgba(245, 158, 11, 0.3)"
                                      : "rgba(239, 68, 68, 0.3)",
                              }}
                            />
                          </Box>

                          {/* Email Verification Status - Only show if not verified */}
                          {user.activation_link && user.activation_link !== '' && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <EmailIcon sx={{ fontSize: 16, color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)' }} titleAccess="Email Verification" />
                              <Chip
                                label="Not Verified"
                                size="small"
                                sx={{
                                  fontSize: "0.7rem",
                                  height: 22,
                                  background: isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.2)",
                                  color: isLight ? "#DC2626" : "#EF4444",
                                  fontWeight: 600,
                                  border: isLight ? '1.5px solid' : '1px solid',
                                  borderColor: isLight ? "#FECACA" : "rgba(239, 68, 68, 0.3)",
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>{formatDate(user.created_at)}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box display="flex" gap={0.5} alignItems="center">
                          {/* View User Details (Requirement 1) */}
                          <Tooltip title="View User Details">
                            <IconButton
                              onClick={() => handleOpenUserDetails(user)}
                              size="small"
                              sx={{
                                color: isLight ? '#0D9488' : '#4CA1AF',
                                '&:hover': { background: isLight ? '#F0FDFA' : 'rgba(76, 161, 175, 0.15)' }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* View User Activity */}
                          <Tooltip title="View User Activity">
                            <IconButton
                              onClick={() => {
                                const targetUserId = user?.app_users?.id || user?.user_id || user?.id;
                                const userName = `${user?.app_users?.first_name || ''} ${user?.app_users?.last_name || ''}`.trim() || user?.app_users?.email;
                                const userEmail = user?.app_users?.email || '';
                                const userAvatar = user?.app_users?.profile_pic || '';
                                setSelectedActivityUser({
                                  id: targetUserId,
                                  name: userName,
                                  email: userEmail,
                                  avatar: userAvatar
                                });
                                setActivityUserFilter(userEmail || userName);
                                setActiveView('activity');
                              }}
                              size="small"
                              sx={{
                                color: '#7C3AED',
                                '&:hover': { background: isLight ? 'rgba(124, 58, 237, 0.1)' : 'rgba(124, 58, 237, 0.2)' }
                              }}
                            >
                              <TimelineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* Edit */}
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => handleEdit(user.id)}
                              size="small"
                              sx={{
                                color: isLight ? '#0284C7' : '#94A3B8',
                                '&:hover': { background: isLight ? '#F0F9FF' : 'rgba(255, 255, 255, 0.08)' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>



                          {(
                            (user?.app_users?.group_users?.length > 0) ||
                            (user.account_status === "Accepted" && user.isVerified == 1)
                          ) && (
                              <>
                                <IconButton
                                  onClick={() => handleRoleClick(user)}
                                  size="small"
                                  sx={{ color: '#6366F1', '&:hover': { background: 'rgba(99, 102, 241, 0.1)' } }}
                                  title="Manage Role"
                                >
                                  <ManageAccountsIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}

                          {currentUser?.role !== 5 && (

                            <>
                              {user.account_status === "Accepted" ? (
                                <IconButton
                                  onClick={() => handleActivate(user.id, 'AV', 'Declined', user.user_id)}
                                  size="small"
                                  sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                                  title="Deactivate Account"
                                >
                                  <PersonOffIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                <IconButton
                                  onClick={() => handleActivate(user.id, 'AV', 'Accepted', user.user_id)}
                                  size="small"
                                  sx={{ color: '#10B981', '&:hover': { background: 'rgba(16, 185, 129, 0.1)' } }}
                                  title="Activate Account"
                                >
                                  <PersonAddIcon fontSize="small" />
                                </IconButton>
                              )}

                              {/* Background Verification Actions */}
                              {user.isVerified === 0 ? (
                                // Pending: Show Approve and Reject
                                <>
                                  <IconButton
                                    onClick={() => handleActivate(user.id, 'BV', 'Verified', user.user_id)}
                                    size="small"
                                    sx={{ color: '#10B981', '&:hover': { background: 'rgba(16, 185, 129, 0.1)' } }}
                                    title="Approve Background"
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => handleActivate(user.id, 'BV', 'Declined', user.user_id)}
                                    size="small"
                                    sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                                    title="Decline Background"
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </>
                              ) : user.isVerified === 1 ? (
                                // Verified: Show Revoke
                                <IconButton
                                  onClick={() => handleActivate(user.id, 'BV', 'Declined', user.user_id)}
                                  size="small"
                                  sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                                  title="Revoke Verification"
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                // Declined: Show Approve
                                <IconButton
                                  onClick={() => handleActivate(user.id, 'BV', 'Verified', user.user_id)}
                                  size="small"
                                  sx={{ color: '#10B981', '&:hover': { background: 'rgba(16, 185, 129, 0.1)' } }}
                                  title="Approve Background"
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center" }}>
                      No users found
                    </TableCell>
                  </TableRow>
                )}

              </TableBody>

            </Table>
          </TableContainer>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {users.length > 0 ? (
                users.map((u) => (
                  <Card key={u.id} variant="outlined" sx={{ 
                    borderRadius: 2, 
                    background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.03)',
                    border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
                    boxShadow: isLight ? '0 2px 6px rgba(15, 23, 42, 0.05)' : 'none',
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar
                          src={u.app_users.profile_pic || undefined}
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            background: isLight ? "#F0FDFA" : "linear-gradient(135deg, #203A43 0%, #2C5364 100%)",
                            color: isLight ? "#0D9488" : "#4CA1AF",
                            fontWeight: 700,
                            fontSize: 20,
                            border: isLight ? "1.5px solid #99F6E4" : "1px solid rgba(76, 161, 175, 0.3)",
                          }}
                          imgProps={{
                            onError: (e) => { e.target.onerror = null; e.target.src = ""; }
                          }}
                        >
                          {u.app_users.first_name ? u.app_users.first_name[0].toUpperCase() : <PersonIcon sx={{ fontSize: 24 }} />}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={700} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>{u.app_users.first_name} {u.app_users.last_name}</Typography>
                          <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)' }}>{u.app_users.email}</Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: isLight ? '#94A3B8' : 'rgba(255,255,255,0.5)' }} display="block">Organization</Typography>
                        <Typography variant="body2" sx={{ color: isLight ? '#0F172A' : '#FFFFFF', fontWeight: 600 }}>{u.organisations?.org_name || "N/A"}</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? '#94A3B8' : 'rgba(255,255,255,0.5)' }} display="block">Role</Typography>
                          <Chip label={getDisplayRoleName(u.roles?.role, u.role) || "N/A"} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', borderColor: isLight ? '#94A3B8' : 'rgba(255,255,255,0.2)', color: isLight ? '#334155' : 'rgba(255,255,255,0.8)' }} />
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ color: isLight ? '#94A3B8' : 'rgba(255,255,255,0.5)' }} display="block">Status</Typography>
                          <Chip 
                            label={u.account_status} 
                            color={u.account_status === 'Accepted' ? 'success' : 'warning'} 
                            size="small" 
                            sx={{ height: 20, fontSize: '0.65rem' }} 
                          />
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5, borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.05)' }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: isLight ? '#94A3B8' : 'rgba(255,255,255,0.4)' }}>
                          Joined: {formatDate(u.created_at)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" sx={{ color: isLight ? '#0D9488' : '#4CA1AF' }} onClick={() => handleOpenUserDetails(u)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Activity">
                            <IconButton
                              size="small"
                              sx={{ color: '#7C3AED' }}
                              onClick={() => {
                                const email = u?.app_users?.email;
                                const name = `${u?.app_users?.first_name || ''} ${u?.app_users?.last_name || ''}`.trim();
                                setActivityUserFilter(email || name);
                                setActiveView('activity');
                              }}
                            >
                              <TimelineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton size="small" sx={{ color: '#6366F1' }} onClick={() => handleRoleClick(u)}>
                            <ManageAccountsIcon fontSize="small" />
                          </IconButton>
                          {currentUser?.role !== 5 && (
                            <IconButton size="small" sx={{ color: u.account_status === 'Accepted' ? '#EF4444' : '#10B981' }} 
                              onClick={() => handleActivate(u.id, 'AV', u.account_status === 'Accepted' ? 'Declined' : 'Accepted', u.user_id)}>
                              {u.account_status === 'Accepted' ? <PersonOffIcon fontSize="small" /> : <PersonAddIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Typography align="center" sx={{ py: 4, color: 'rgba(255,255,255,0.5)' }}>No users found</Typography>
              )}
            </Box>
          </Box>
          {/* Pagination */}
          {totalItems > 0 && (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
                <Typography variant="body2">
                  Showing {page * pageSize - pageSize + 1}-{Math.min(page * pageSize, totalItems)} of {totalItems}
                </Typography>
                <Box>
                  <Button variant="outlined" sx={{ minWidth: 40, mx: 1, border: isLight ? '1.5px solid #CBD5E1' : undefined, color: isLight ? '#0F172A' : undefined }} onClick={() => setPage(page - 1)} disabled={page === 1}>{'<'}</Button>
                  <Button variant="contained" sx={{ minWidth: 40, mx: 1, background: isLight ? 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)' : undefined, fontWeight: 700 }} onClick={() => setPage(page)}>{page}</Button>
                  <Button variant="outlined" sx={{ minWidth: 40, mx: 1, border: isLight ? '1.5px solid #CBD5E1' : undefined, color: isLight ? '#0F172A' : undefined }} onClick={() => setPage(page + 1)} disabled={page === totalPages}>{'>'}</Button>
                </Box>
              </Box>


            </>

          )}
        </Paper>
        </>
        )}

        {editUser && (
          <Dialog
            open={!!editUser}
            onClose={() => setEditUser(null)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                background: isLight ? '#FFFFFF' : 'rgba(30, 41, 59, 0.98)',
                border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
                borderRadius: 2.5,
                color: isLight ? '#0F172A' : '#FFFFFF',
              }
            }}
          >
            <DialogTitle sx={{ fontWeight: 700, color: isLight ? '#0F172A' : '#FFFFFF' }}>Edit User</DialogTitle>
            <DialogContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                <TextField
                  label="First Name"
                  value={
                    editUser.app_users.first_name && editUser.app_users.first_name !== "null"
                      ? editUser.app_users.first_name
                      : ""
                  }
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^A-Za-z\s]/g, "");
                    setEditUser({
                      ...editUser,
                      app_users: { ...editUser.app_users, first_name: val },
                    });
                  }}
                  error={!!editErrors.first_name}
                  helperText={editErrors.first_name}
                />
                <TextField
                  label="Last Name"
                  value={
                    editUser.app_users.last_name && editUser.app_users.last_name !== "null"
                      ? editUser.app_users.last_name
                      : ""
                  }
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^A-Za-z\s]/g, "");
                    setEditUser({
                      ...editUser,
                      app_users: { ...editUser.app_users, last_name: val },
                    });
                  }}
                  error={!!editErrors.last_name}
                  helperText={editErrors.last_name}
                />
                <TextField
                  label="Phone"
                  value={
                    editUser.app_users.phone && editUser.app_users.phone !== "null"
                      ? editUser.app_users.phone
                      : ""
                  }
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, "");
                    setEditUser({
                      ...editUser,
                      app_users: { ...editUser.app_users, phone: val },
                    });
                  }}
                  error={!!editErrors.phone}
                  helperText={editErrors.phone}
                />
                <TextField
                  label="Email"
                  InputProps={{
                    readOnly: true,
                  }}
                  value={
                    editUser.app_users.email && editUser.app_users.email !== "null"
                      ? editUser.app_users.email
                      : ""
                  }
                  onChange={(e) =>
                    setEditUser({
                      ...editUser,
                      app_users: { ...editUser.app_users, email: e.target.value },
                    })
                  }
                  error={!!editErrors.email}
                  helperText={editErrors.email}
                />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <label>Gender:</label>
                  <Select
                    label="Gender"
                    value={
                      editUser.app_users.gender && editUser.app_users.gender !== "null"
                        ? editUser.app_users.gender
                        : ""
                    }
                    onChange={(e) =>
                      setEditUser({
                        ...editUser,
                        app_users: { ...editUser.app_users, gender: e.target.value },
                      })
                    }
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <label>Baptized:</label>
                  <Select
                    label="Baptized"
                    value={
                      editUser.app_users.baptized && editUser.app_users.baptized !== "null"
                        ? editUser.app_users.baptized
                        : ""
                    }
                    onChange={(e) =>
                      setEditUser({
                        ...editUser,
                        app_users: { ...editUser.app_users, baptized: e.target.value },
                      })
                    }
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <TextField
                    label="Date of Birth"
                    type="date"
                    value={
                      editUser.app_users.date_of_birth && editUser.app_users.date_of_birth !== "null"
                        ? editUser.app_users.date_of_birth
                        : ""
                    }
                    onChange={(e) =>
                      setEditUser({
                        ...editUser,
                        app_users: { ...editUser.app_users, date_of_birth: e.target.value },
                      })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <TextField
                  label="Address"
                  value={
                    editUser.app_users.address && editUser.app_users.address !== "null"
                      ? editUser.app_users.address
                      : ""
                  }
                  onChange={(e) =>
                    setEditUser({
                      ...editUser,
                      app_users: { ...editUser.app_users, address: e.target.value },
                    })
                  }
                />
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Profile Picture
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Validate size before setting
                        if (file.size > 200 * 1024) {
                          setEditErrors((prev) => ({
                            ...prev,
                            profile_pic: "Image size should be less than 200KB",
                          }));
                        } else {
                          setEditErrors((prev) => ({ ...prev, profile_pic: undefined }));
                          setEditUser({
                            ...editUser,
                            app_users: { ...editUser.app_users, profile_pic: file },
                          });
                        }
                      }
                    }}
                  />
                  {editErrors.profile_pic && (
                    <Typography color="error" variant="caption">
                      {editErrors.profile_pic}
                    </Typography>
                  )}
                  {editUser.app_users.profile_pic &&
                    typeof editUser.app_users.profile_pic === "string" && (
                      <Box sx={{ mt: 1 }}>
                        <img
                          src={editUser.app_users.profile_pic}
                          alt="Logo"
                          style={{ maxWidth: 80, maxHeight: 80, borderRadius: 4 }}
                        />
                      </Box>
                    )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}></Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button variant="contained" onClick={handleSaveEdit}>
                  Save
                </Button>
                <Button variant="outlined" onClick={() => setEditUser(null)} sx={{ ml: 2 }}>
                  Cancel
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
        )}

        <Dialog
          open={openViewDialog}
          onClose={handleViewDialogClose}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              background: isLight ? '#FFFFFF' : 'rgba(30, 41, 59, 0.98)',
              border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2.5,
              color: isLight ? '#0F172A' : '#FFFFFF',
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: isLight ? '#0F172A' : '#FFFFFF' }}>
            {Bulkstatus === 1
              ? "Background Verify User List"
              : Bulkstatus === 2
                ? "Background Decline List"
                : Bulkstatus === 4
                  ? "Account Activate List"
                  : "Account Deactivate List"}
          </DialogTitle>
          <DialogContent>
            {selectedUsers.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No User selected.
              </Typography>
            )}
            {selectedUsers.map((user, index) => (
              <Typography key={user.id} variant="h6">
                {index + 1}. {user.name}
              </Typography>
            ))}
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1, borderTop: isLight ? '1.5px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)' }}>
            <Button 
              onClick={handleViewDialogClose}
              variant="outlined"
              sx={{
                borderColor: isLight ? '#CBD5E1' : 'rgba(255, 255, 255, 0.2)',
                color: isLight ? '#475569' : '#FFFFFF',
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={BulkUpdateToUser} 
              variant="contained"
              sx={{
                background: isLight ? 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)' : undefined,
                color: '#FFFFFF',
                fontWeight: 700,
                textTransform: 'none',
                px: 3,
                boxShadow: isLight ? '0 2px 6px rgba(13, 148, 136, 0.3)' : undefined,
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Details Modal (Requirement 1) */}
        <Dialog
          open={userDetailsOpen}
          onClose={handleCloseUserDetails}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              background: isLight ? '#FFFFFF' : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              backdropFilter: 'blur(20px)',
              border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(76, 161, 175, 0.35)',
              borderRadius: 3,
              color: isLight ? '#0F172A' : '#FFFFFF',
              boxShadow: isLight ? '0 20px 40px rgba(15, 23, 42, 0.12)' : '0 20px 50px rgba(0, 0, 0, 0.5)',
              p: { xs: 1, sm: 2 }
            }
          }}
        >
          {selectedUserForDetails && (
            <>
              {/* Modal Header with Close Button */}
              <DialogTitle sx={{ p: 2, pb: 1, borderBottom: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.08)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={selectedUserForDetails.app_users?.profile_pic || undefined}
                      alt={selectedUserForDetails.app_users?.first_name}
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2.5,
                        background: isLight ? '#F0FDFA' : 'linear-gradient(135deg, #203A43 0%, #2C5364 100%)',
                        color: isLight ? '#0D9488' : '#4CA1AF',
                        fontWeight: 700,
                        fontSize: 24,
                        border: isLight ? '2px solid #CCFBF1' : '2px solid rgba(76, 161, 175, 0.4)',
                        boxShadow: isLight ? '0 2px 8px rgba(13, 148, 136, 0.15)' : '0 4px 12px rgba(0,0,0,0.3)',
                      }}
                      imgProps={{
                        onError: (e) => { e.target.onerror = null; e.target.src = ""; }
                      }}
                    >
                      {selectedUserForDetails.app_users?.first_name 
                        ? selectedUserForDetails.app_users.first_name[0].toUpperCase() 
                        : <PersonIcon sx={{ fontSize: 28 }} />}
                    </Avatar>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h6" fontWeight={700} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                          {selectedUserForDetails.app_users?.first_name} {selectedUserForDetails.app_users?.last_name}
                        </Typography>
                        <Chip
                          label={
                            selectedUserForDetails.account_status === "Accepted"
                              ? "Active"
                              : selectedUserForDetails.account_status === "Pending"
                                ? "Pending"
                                : "Inactive"
                          }
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            background: isLight
                              ? selectedUserForDetails.account_status === "Accepted"
                                ? '#ECFDF5'
                                : selectedUserForDetails.account_status === "Pending"
                                  ? '#FFFBEB'
                                  : '#FEF2F2'
                              : selectedUserForDetails.account_status === "Accepted" 
                              ? 'rgba(16, 185, 129, 0.2)' 
                              : selectedUserForDetails.account_status === "Pending" 
                              ? 'rgba(245, 158, 11, 0.2)' 
                              : 'rgba(239, 68, 68, 0.2)',
                            color: isLight
                              ? selectedUserForDetails.account_status === "Accepted"
                                ? '#059669'
                                : selectedUserForDetails.account_status === "Pending"
                                  ? '#D97706'
                                  : '#DC2626'
                              : selectedUserForDetails.account_status === "Accepted" 
                              ? '#10B981' 
                              : selectedUserForDetails.account_status === "Pending" 
                              ? '#F59E0B' 
                              : '#EF4444',
                            border: '1px solid currentColor'
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.65)', mt: 0.2 }}>
                        {selectedUserForDetails.app_users?.email}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Clear Close / X Option */}
                  <IconButton
                    onClick={handleCloseUserDetails}
                    sx={{
                      color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.6)',
                      background: isLight ? '#F1F5F9' : 'rgba(255, 255, 255, 0.05)',
                      border: isLight ? '1px solid #CBD5E1' : undefined,
                      '&:hover': {
                        color: isLight ? '#DC2626' : '#FFFFFF',
                        background: isLight ? '#FEE2E2' : 'rgba(239, 68, 68, 0.25)',
                      }
                    }}
                    title="Close"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>

              {/* Modal Body: Complete User Information */}
              <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: 2 }}>
                <Grid container spacing={2.5}>

                  {/* Section 1: Personal & Contact Information */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)',
                        border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.08)',
                        height: '100%'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <PersonIcon sx={{ color: isLight ? '#0D9488' : '#4CA1AF', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isLight ? '#0D9488' : '#4CA1AF', letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Personal & Contact Info
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.5)' }}>Full Name</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                            {selectedUserForDetails.app_users?.first_name} {selectedUserForDetails.app_users?.last_name}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.5)' }}>Email Address</Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                              {selectedUserForDetails.app_users?.email || 'N/A'}
                            </Typography>
                          </Box>
                          {selectedUserForDetails.app_users?.email && (
                            <Tooltip title="Copy Email">
                              <IconButton 
                                size="small" 
                                onClick={() => handleCopyText(selectedUserForDetails.app_users?.email, 'Email')}
                                sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)', '&:hover': { color: isLight ? '#0D9488' : '#4CA1AF' } }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.5)' }}>Phone Number</Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                              {selectedUserForDetails.app_users?.phone && selectedUserForDetails.app_users?.phone !== 'null'
                                ? selectedUserForDetails.app_users.phone
                                : 'Not provided'}
                            </Typography>
                          </Box>
                          {selectedUserForDetails.app_users?.phone && selectedUserForDetails.app_users?.phone !== 'null' && (
                            <Tooltip title="Copy Phone">
                              <IconButton 
                                size="small" 
                                onClick={() => handleCopyText(selectedUserForDetails.app_users?.phone, 'Phone')}
                                sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)', '&:hover': { color: isLight ? '#0D9488' : '#4CA1AF' } }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.5)' }}>Gender</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                            {selectedUserForDetails.app_users?.gender && selectedUserForDetails.app_users?.gender !== 'null'
                              ? selectedUserForDetails.app_users.gender
                              : 'Not specified'}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.5)' }}>System User ID</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: isLight ? '#475569' : '#94A3B8' }}>
                            #{selectedUserForDetails.id} (App ID: #{selectedUserForDetails.user_id || selectedUserForDetails.app_users?.id || 'N/A'})
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Section 2: Organization & Role Information */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)',
                        border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.08)',
                        height: '100%'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <BusinessIcon sx={{ color: isLight ? '#0D9488' : '#4CA1AF', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isLight ? '#0D9488' : '#4CA1AF', letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Organization & Roles
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.5)' }}>Organization Name</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                            {selectedUserForDetails.origanisation?.org_name || selectedUserForDetails.organisations?.org_name || "Glocal Prayer Network"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.5)' }}>System Assigned Role</Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={getDisplayRoleName(selectedUserForDetails.roles?.role, selectedUserForDetails.role)}
                              size="small"
                              sx={{
                                background: isLight ? '#EEF2FF' : 'rgba(99, 102, 241, 0.2)',
                                color: isLight ? '#4F46E5' : '#A5B4FC',
                                fontWeight: 700,
                                border: isLight ? '1px solid #C7D2FE' : '1px solid rgba(99, 102, 241, 0.35)',
                              }}
                            />
                          </Box>
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.5)' }}>Account Status</Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={selectedUserForDetails.account_status}
                              size="small"
                              color={selectedUserForDetails.account_status === 'Accepted' ? 'success' : 'warning'}
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.5)' }}>Background Verification</Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ color: selectedUserForDetails.isVerified === 1 ? '#10B981' : '#F59E0B' }}>
                            {selectedUserForDetails.isVerified === 1 ? '✓ Verified' : '⏳ Pending Verification'}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.5)' }}>Account Created / Joined</Typography>
                          <Typography variant="body2" sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                            {formatDate(selectedUserForDetails.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Section 3: Group Roles & Memberships (Full Width) */}
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)',
                        border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.08)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <GroupsIcon sx={{ color: isLight ? '#0D9488' : '#4CA1AF', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isLight ? '#0D9488' : '#4CA1AF', letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Assigned Groups & Sub-Roles
                        </Typography>
                      </Box>

                      {selectedUserForDetails.app_users?.group_users && selectedUserForDetails.app_users.group_users.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedUserForDetails.app_users.group_users.map((gu, idx) => (
                            <Chip
                              key={idx}
                              icon={<GroupsIcon sx={{ fontSize: '16px !important' }} />}
                              label={`${gu.groups?.name || 'Group'}: ${getDisplayRoleName(gu.roles?.role, gu.role_id)}`}
                              size="small"
                              sx={{
                                background: isLight ? '#F0FDFA' : 'rgba(76, 161, 175, 0.15)',
                                color: isLight ? '#0D9488' : '#4CA1AF',
                                border: isLight ? '1px solid #99F6E4' : '1px solid rgba(76, 161, 175, 0.3)',
                                fontWeight: 600,
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
                          No specific sub-groups assigned to this user.
                        </Typography>
                      )}
                    </Paper>
                  </Grid>

                </Grid>
              </DialogContent>

              {/* Modal Footer Actions */}
              <DialogActions sx={{ p: 2, pt: 1, borderTop: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.08)', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      const id = selectedUserForDetails.id;
                      handleCloseUserDetails();
                      handleEdit(id);
                    }}
                    sx={{
                      color: isLight ? '#334155' : 'rgba(255,255,255,0.8)',
                      borderColor: isLight ? '#94A3B8' : 'rgba(255,255,255,0.2)',
                      borderWidth: '1.5px',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { borderColor: isLight ? '#0D9488' : '#4CA1AF', color: isLight ? '#0D9488' : '#4CA1AF' }
                    }}
                  >
                    Edit User
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TimelineIcon />}
                    onClick={() => {
                      const u = selectedUserForDetails;
                      handleCloseUserDetails();
                      const targetUserId = u?.app_users?.id || u?.user_id || u?.id;
                      const userName = `${u?.app_users?.first_name || ''} ${u?.app_users?.last_name || ''}`.trim() || u?.app_users?.email;
                      const userEmail = u?.app_users?.email || '';
                      const userAvatar = u?.app_users?.profile_pic || '';
                      setSelectedActivityUser({
                        id: targetUserId,
                        name: userName,
                        email: userEmail,
                        avatar: userAvatar
                      });
                      setActivityUserFilter(userEmail || userName);
                      setActiveView("activity");
                    }}
                    sx={{
                      color: '#7C3AED',
                      borderColor: '#7C3AED',
                      borderWidth: '1.5px',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { background: 'rgba(124, 58, 237, 0.1)', borderColor: '#6D28D9' }
                    }}
                  >
                    View User Activity
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ManageAccountsIcon />}
                    onClick={() => {
                      const u = selectedUserForDetails;
                      handleCloseUserDetails();
                      handleRoleClick(u);
                    }}
                    sx={{
                      color: isLight ? '#4F46E5' : '#A5B4FC',
                      borderColor: isLight ? '#818CF8' : 'rgba(99, 102, 241, 0.35)',
                      borderWidth: '1.5px',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { background: isLight ? '#EEF2FF' : 'rgba(99, 102, 241, 0.15)' }
                    }}
                  >
                    Manage Roles
                  </Button>
                </Box>

                <Button
                  variant="contained"
                  onClick={handleCloseUserDetails}
                  sx={{
                    background: isLight ? 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)' : 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 3,
                    boxShadow: isLight ? '0 2px 6px rgba(13, 148, 136, 0.25)' : '0 4px 12px rgba(76, 161, 175, 0.3)',
                    '&:hover': {
                      background: isLight ? 'linear-gradient(135deg, #0F766E 0%, #115E59 100%)' : 'linear-gradient(135deg, #3b8e9b 0%, #1f2d3a 100%)',
                    }
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>




      </Box>
      <Toast toast={toast} setToast={setToast} />
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        severity="warning"
      />
    </>
  );
};

export default Users;